import { FormEvent, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import styled, { css, keyframes } from "styled-components"

import { useUpdateClientMutation } from "store/api/clientApi"
import { useLazyGetDeviceLocationsQuery, useLazyGetResidentTypesQuery } from "store/api/dictionaryApi"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconDevice } from "assets/svg/icon-device.svg"

import { TransferModal } from "modals/EditCustomerModal/TransferModal"
import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import { ButtonNew } from "components/form/ButtonNew"
import { InputNew } from "components/form/InputNew"
import { SelectDynamic } from "components/form/SelectDynamic"
import { SelectStatic } from "components/form/SelectStatic"

import { useCustomerDashboard } from "../CustomerDashboardContext"

const DevicesDataWrapper = styled.section`
  margin-bottom: 24px;
`

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  padding: 0 20px 20px;
`

const FieldItem = styled.div`
  flex: 1 0 calc(33.33% - 20px);
  min-width: 200px;
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldLabel = styled.h4`
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #0f172a;
  margin: 0 0 4px 0;
`

const SerialNumberWrapper = styled.div`
  display: flex;
  align-items: center;
`

const SerialNumberValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #94a3b8;
  padding: 10px 0 10px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const ReadOnlyValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #94a3b8;
  padding: 10px 0;
`

const WorkStatusValue = styled.div<{ isOnline: boolean }>`
  font-size: 14px;
  font-weight: 400;
  color: ${({ isOnline }) => (isOnline ? "#22c55e" : "#94a3b8")};
  padding: 10px 0;
  margin-left: 5px;
  text-transform: capitalize;
`

const transferPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(45, 178, 200, 0.5);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 12px 4px rgba(45, 178, 200, 0.4);
  }
`

const TransferButtonWrapper = styled.div<{ $highlight?: boolean }>`
  margin-left: auto;

  ${({ $highlight }) =>
    $highlight &&
    css`
      animation: ${transferPulse} 1s ease-in-out 6;
      border-radius: 20px;

      button {
        background-color: #2db2c8 !important;
        color: #fff !important;
      }
    `}
`

const TransferButton = styled(ButtonNew)`
  color: #334155 !important;
  font-weight: 400;

  &:hover {
    opacity: 0.7;
  }
`

const getInstallationType = (device: UITypes.Device): string => {
  const type = device.installationType
  if (type) {
    if (typeof type === "string") {
      return type
    }
    return type[0]?.value as string
  }
  return ""
}

interface DeviceInformationProps {
  deviceValues: UITypes.Device
  setDeviceValues: (values: UITypes.Device) => void
  originalDevice: ApiTypes.Model.Device
  selectedDevice: ApiTypes.Model.Device
  errors: { deviceName?: string; address1?: string }
  setErrors: (errors: { deviceName?: string; address1?: string }) => void
}

export const DeviceInformation = ({
  deviceValues,
  setDeviceValues,
  originalDevice,
  selectedDevice,
  errors,
  setErrors,
}: DeviceInformationProps) => {
  const { customer, onUpdateSuccess, setCurrentDevice, highlightTransfer, setHighlightTransfer } =
    useCustomerDashboard()
  const [updateClient, { isLoading }] = useUpdateClientMutation()
  const [showTransferModal, setShowTransferModal] = useState(false)
  const transferButtonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (highlightTransfer && transferButtonRef.current) {
      transferButtonRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
      const timer = setTimeout(() => setHighlightTransfer(false), 6000)
      return () => clearTimeout(timer)
    }
  }, [highlightTransfer, setHighlightTransfer])

  const onDeviceValuesChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setDeviceValues({ ...deviceValues, [name]: value })
    if (errors[name as keyof typeof errors]) {
      setErrors({ ...errors, [name]: undefined })
    }
  }

  const handleTransferSuccess = () => {
    setShowTransferModal(false)
    setCurrentDevice(0)
  }

  const handleSaveDevice = async () => {
    if (!customer || !deviceValues || !originalDevice) return

    const validationErrors: { deviceName?: string; address1?: string } = {}

    if (!deviceValues.deviceName || deviceValues.deviceName.trim() === "") {
      validationErrors.deviceName = "Device name is required"
    }

    const addressToValidate = originalDevice.address1 || deviceValues.address1 || ""
    if (!addressToValidate || addressToValidate.trim() === "") {
      validationErrors.address1 = "Address is required"
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors({})

    try {
      const customerId = customer.id || 0

      const installationType = getInstallationType(deviceValues)
      const editDevices: ApiTypes.Req.EditCustomerDevice[] = [
        {
          sn: originalDevice.sn,
          name: deviceValues.deviceName || "",
          address1: originalDevice.address1 || deviceValues.address1 || "",
          address2: originalDevice.address2 || "",
          state: originalDevice.state?.id,
          city: originalDevice.city?.id,
          country_id: originalDevice.country?.id || 0,
          zip_code: originalDevice.zip?.code || "",
          where_installed_id: deviceValues.deviceLocation?.[0]
            ? Number(deviceValues.deviceLocation[0].value)
            : originalDevice.location?.id || 0,
          resident_type_id: deviceValues.residentialType?.[0]
            ? Number(deviceValues.residentialType[0].value)
            : originalDevice.residence_type?.id || 0,
          installation_type: installationType,
          ...(deviceValues.systemAge?.[0] ? { system_age: Number(deviceValues.systemAge[0].value) } : {}),
        },
      ]

      await updateClient({
        id: customerId,
        body: {
          devices: editDevices,
        } as unknown as ApiTypes.Req.ClientDevices,
      }).unwrap()

      toast.success("Device information updated successfully")
      onUpdateSuccess()
    } catch {
      toast.error("Failed to update device information")
    }
  }

  return (
    <>
      <DevicesDataWrapper>
        <ModalSectionTitle title="Device Information" Icon={IconDevice}>
          <ButtonNew onClick={handleSaveDevice} disabled={isLoading} color="blue" text="Save" />
        </ModalSectionTitle>
        <FieldRow>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Device name</FieldLabel>
              <InputNew
                placeholder="Enter Device name"
                name="deviceName"
                value={deviceValues.deviceName || ""}
                onChange={onDeviceValuesChange}
                errorMessage={errors.deviceName || ""}
              />
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>S/N</FieldLabel>
              <SerialNumberWrapper>
                <SerialNumberValue>{deviceValues.serialNumber || "-"}</SerialNumberValue>
                <TransferButtonWrapper ref={transferButtonRef} $highlight={highlightTransfer}>
                  <TransferButton
                    onClick={() => {
                      setHighlightTransfer(false)
                      setShowTransferModal(true)
                    }}
                    size="auto"
                  >
                    Transfer
                  </TransferButton>
                </TransferButtonWrapper>
              </SerialNumberWrapper>
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Firmware version</FieldLabel>
              <ReadOnlyValue>
                {(selectedDevice as ApiTypes.Model.Device & { firmware_version?: string })?.firmware_version || "-"}
              </ReadOnlyValue>
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Work status</FieldLabel>
              <WorkStatusValue isOnline={deviceValues.workStatus === "online"}>
                {deviceValues.workStatus || "offline"}
              </WorkStatusValue>
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Residence type</FieldLabel>
              <SelectDynamic
                placeholder="Select residence type"
                useLazyQuery={useLazyGetResidentTypesQuery}
                valueName="id"
                labelName="name"
                values={deviceValues.residentialType || []}
                onChange={value => setDeviceValues({ ...deviceValues, ...{ residentialType: value } })}
                errorMessage=""
              />
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Installation Type</FieldLabel>
              <SelectStatic
                placeholder="Select Type"
                values={deviceValues.installationType as UITypes.Option[]}
                isSearchable={false}
                options={[
                  {
                    value: "new",
                    label: "New",
                  },
                  {
                    value: "existing",
                    label: "Existing",
                  },
                ]}
                onChange={value => setDeviceValues({ ...deviceValues, ...{ installationType: value } })}
                errorMessage=""
              />
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>Device location</FieldLabel>
              <SelectDynamic
                placeholder="Select device location"
                useLazyQuery={useLazyGetDeviceLocationsQuery}
                valueName="id"
                labelName="name"
                queryParams={{ rt: deviceValues.residentialType?.[0]?.label?.toLowerCase() || "" }}
                values={deviceValues.deviceLocation || []}
                onChange={value => setDeviceValues({ ...deviceValues, ...{ deviceLocation: value } })}
                errorMessage=""
              />
            </FieldWrapper>
          </FieldItem>
          <FieldItem>
            <FieldWrapper>
              <FieldLabel>HVAC system age</FieldLabel>
              <SelectStatic
                placeholder="Select system age"
                values={deviceValues.systemAge || []}
                options={Array.from({ length: 30 }, (_, i) => ({
                  value: (i + 1).toString(),
                  label: `${i + 1} ${i + 1 === 1 ? "Year" : "Years"}`,
                }))}
                onChange={value => setDeviceValues({ ...deviceValues, ...{ systemAge: value } })}
                errorMessage=""
              />
            </FieldWrapper>
          </FieldItem>
          <FieldItem />
        </FieldRow>
      </DevicesDataWrapper>

      {showTransferModal && (
        <TransferModal
          serialNumber={deviceValues.serialNumber}
          onCancel={() => setShowTransferModal(false)}
          onSuccess={handleTransferSuccess}
        />
      )}
    </>
  )
}
